import { useMemo } from 'react';

import { useABSmartlyContext } from '../provider/ABSmartlyProvider';
import { getVariantsArray } from '../utils/variants';

type ReturnType<TVariants extends readonly string[]> = {
  isReady: boolean;
  results: (TVariants[number] | null)[];
  variables?: Record<string, string | undefined>;
};

export const useABSmartlyExperiments = <TVariants extends readonly string[]>(
  experimentIds: string | string[],
  experimentVariables?: string[],
): ReturnType<TVariants> => {
  const context = useABSmartlyContext();

  return useMemo(() => {
    const evaluateExperiment = (experimentId: string) => {
      if (!experimentId || !context || !context.isReady()) {
        return null;
      }
      const variant = context.treatment(experimentId);
      const variants = getVariantsArray(context, experimentId);
      const variantName = variants[variant];
      return variantName;
    };
    const results = Array.isArray(experimentIds)
      ? experimentIds.map(experimentId => evaluateExperiment(experimentId))
      : [evaluateExperiment(experimentIds)];

    const variables = experimentVariables?.reduce(
      (acc, variable) => {
        acc[variable] = context?.variableValue(variable, '');
        return acc;
      },
      {} as Record<string, string | undefined>,
    );

    return {
      isReady: !!context?.isReady(),
      results,
      variables,
    };
  }, [context, experimentIds]);
};
